import { format } from 'date-fns'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { FieldTypes, WellYieldDrawdownFields, WellYieldDrawdownFieldsEdit } from '../../../../types/form-types'
import { AddField, RemoveField, SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import DrawdownTestStepEditCard from './step-edit-card'
import { DateField, Divider, FormField, Heading, RecordContent, RepeatableSection } from '@msaf/core-react'

interface DrillingTestEditFormProps {
  isLoading: boolean
  fields: Partial<WellYieldDrawdownFields>
  setValue: SetValue<WellYieldDrawdownFields>
  removeField: RemoveField<WellYieldDrawdownFields>
  addField: AddField<WellYieldDrawdownFields>
  validationErrors?: ValidationErrors<WellYieldDrawdownFields>
}

export function DrillingTestEditForm({
  isLoading,
  fields,
  setValue,
  removeField,
  addField,
  validationErrors = {},
}: DrillingTestEditFormProps) {
  return (
    <RecordContent>
      <Heading isSkeleton={isLoading} level={2}>
        Yield drawdown test
      </Heading>

      <Heading isSkeleton={isLoading} level={3}>
        Details
      </Heading>

      <FormField
        isSkeleton={isLoading}
        label='Test Date'
        labelId='drillingTestDateId'
        validationMessages={validationErrors?.testDate}
        isRequired
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          selectedDate={parseDate(fields.testDate)}
          dateFormat={DISPLAY_DATE_FORMAT}
          setSelectedDate={(date: Date) => setValue('testDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
          isInvalid={!!validationErrors?.testDate}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Flow rate unit'
        labelId='pumpingRateUnit'
        isRequired
        validationMessages={validationErrors?.pumpingRateUnit as Array<string>}
      >
        <LookupField
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          fieldId='pumpingRateUnit'
          labelledBy='pumpingRateUnitLabel'
          value={`${fields.pumpingRateUnit ?? ''}`}
          setValue={({ value }) => setValue('pumpingRateUnit', value)}
          isInvalid={!!validationErrors?.pumpingRateUnit}
        />
      </FormField>

      <Divider />

      <RepeatableSection
        heading='Test steps'
        sectionName='Step'
        removeField={(index: number) => removeField('testSteps', index)}
        addField={() =>
          addField('testSteps', {
            // Set the step number on adding a new step
            stepNo: fields.testSteps?.length ? fields.testSteps?.length + 1 : 1,
          })
        }
        isSkeleton={isLoading}
      >
        {fields?.testSteps &&
          fields.testSteps.map((drawdown: Partial<WellYieldDrawdownFieldsEdit>, index) => {
            return (
              <DrawdownTestStepEditCard
                key={fields.id ?? index}
                drawdownTestFields={drawdown}
                stepNo={drawdown.stepNo ?? index + 1}
                isLoading={isLoading}
                id={index ?? 0}
                setValue={(key: keyof WellYieldDrawdownFieldsEdit, value: FieldTypes) =>
                  setValue('testSteps', value, key, index)
                }
                validationErrors={validationErrors?.testSteps?.[index] as ValidationErrors}
              />
            )
          })}
      </RepeatableSection>
    </RecordContent>
  )
}
